import { Shield, Settings } from "lucide-react";
import { cn } from "@/lib/utils";

interface MsalDisabledOverlayProps {
  /**
   * The message to display. If not provided, uses a default message based on the feature name.
   */
  message?: string;
  /**
   * The title to display. Defaults to "Microsoft Login Required".
   */
  title?: string;
  /**
   * Name of the feature that requires Microsoft login (e.g., "meeting rooms", "holiday calendar").
   * Used to generate a contextual message if message prop is not provided.
   */
  featureName?: string;
  /**
   * Additional CSS classes for the overlay container
   */
  className?: string;
  /**
   * Size variant for the overlay
   */
  size?: "sm" | "md" | "lg";
  /**
   * Show a "Go to Settings" hint
   */
  showSettingsHint?: boolean;
}

export const MsalDisabledOverlay = ({
  message,
  title = "Microsoft Login Required",
  featureName,
  className,
  size = "md",
  showSettingsHint = false,
}: MsalDisabledOverlayProps) => {
  const sizeClasses = {
    sm: {
      icon: "size-6",
      title: "text-sm",
      message: "text-xs",
      padding: "p-4",
      iconPadding: "mb-2",
    },
    md: {
      icon: "size-8",
      title: "text-base",
      message: "text-sm",
      padding: "p-6",
      iconPadding: "mb-3",
    },
    lg: {
      icon: "size-12",
      title: "text-lg",
      message: "text-sm",
      padding: "p-8",
      iconPadding: "mb-4",
    },
  };

  const classes = sizeClasses[size];

  // Generate contextual message if not provided
  const displayMessage =
    message ||
    (featureName
      ? `This feature requires Microsoft login. Enable it in your user settings to access ${featureName}.`
      : "This feature requires Microsoft login. Enable it in your user settings to continue.");

  return (
    <div
      className={cn(
        "absolute inset-0 flex items-start/ justify-center z-[49] bg-background/85 backdrop-blur-sm rounded-lg",
        className
      )}
    >
      <div className={cn("text-center max-w-md", classes.padding)}>
        <div
          className={cn(
            "inline-flex items-center justify-center rounded-full bg-muted/50 p-3",
            classes.iconPadding
          )}
        >
          <Shield className={cn(classes.icon, "text-muted-foreground")} />
        </div>
        <h3
          className={cn(
            classes.title,
            "font-semibold text-foreground mb-2 leading-tight"
          )}
        >
          {title}
        </h3>
        <p
          className={cn(
            classes.message,
            "text-muted-foreground leading-relaxed"
          )}
        >
          {displayMessage}
        </p>
        {showSettingsHint && (
          <div
            className={cn(
              "mt-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground/70 bg-muted/30 px-3 py-1.5 rounded-full",
              size === "sm" && "text-[10px] px-2 py-1 mt-3"
            )}
          >
            <Settings className="size-3" />
            <span>Click your avatar → Enable Microsoft Login</span>
          </div>
        )}
      </div>
    </div>
  );
};
